import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

import { refresh } from "@/api/";
import { parseCommissionDebtError } from "@/lib/validation/commission.schemas";
import { useAuthStore } from "@/stores/auth.store";
import { useCommissionGateStore } from "@/stores/commissionGate.store";
import { notify } from "@/stores/notification.store";
import { UNAVAILABLE_STATUSES, useServiceStatusStore } from "@/stores/serviceStatus.store";

const REQUEST_TIMEOUT_MS = 30_000;

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
  },
});

// A 401 from these means wrong credentials or a dead refresh cookie, not an
// expired access token, so it must reach the caller instead of triggering a refresh.
const AUTH_ENDPOINTS = ["/auth/login", "/auth/register", "/auth/otp/", "/auth/refresh", "/auth/logout"];

const isAuthEndpoint = (url = "") => AUTH_ENDPOINTS.some((path) => url.includes(path));

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

// Requests that 401 at the same time share one refresh call.
let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = () => {
  refreshPromise ??= refresh()
    .then((response) => {
      const accessToken = response.status === 200 ? response.data.data?.access_token : undefined;
      if (!accessToken) throw new Error("Token refresh returned no access token");
      useAuthStore.setState({ accessToken, isAuthenticated: true });
      return accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};

const endSession = () => {
  if (!useAuthStore.getState().isAuthenticated) return;
  useAuthStore.getState().logout();
  notify("نشست شما به پایان رسیده است؛ دوباره وارد شوید", "warning");
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const setServiceUnavailable = (isUnavailable: boolean) => {
  if (useServiceStatusStore.getState().isUnavailable !== isUnavailable) {
    useServiceStatusStore.getState().setUnavailable(isUnavailable);
  }
};

// Only a refresh the server actually rejected ends the session; a refresh that
// failed because the backend is down or unreachable keeps the user signed in.
const isRefreshRejected = (refreshError: unknown) => {
  if (!axios.isAxiosError(refreshError)) return true;
  const status = refreshError.response?.status;
  return status !== undefined && status >= 400 && status < 500;
};

axiosInstance.interceptors.response.use(
  (response) => {
    setServiceUnavailable(false);
    return response;
  },
  async (error: AxiosError) => {
    if (error.response && UNAVAILABLE_STATUSES.includes(error.response.status)) {
      setServiceUnavailable(true);
    }

    const originalRequest = error.config as RetriableRequestConfig | undefined;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url)
    ) {
      originalRequest._retry = true;
      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        if (isRefreshRejected(refreshError)) endSession();
        // Reject with the request's own error so callers see its status, not the refresh's.
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 402 && useAuthStore.getState().isAuthenticated) {
      const debtError = parseCommissionDebtError(error.response.data);
      if (debtError) {
        useCommissionGateStore.getState().openFromError(debtError);
      }
    }

    return Promise.reject(error);
  },
);
